import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { useIntl } from 'react-intl';

import {
  setUserSetting,
  updateUserSettings,
} from '../../actions/Auth';
import {
  clearFiles,
  fetchFiles,
  setFilesParams,
  setFilesLoading,
  clearFilesList,
} from '../../actions/Files';
import {
  setCollectionsParams,
  clearCollectionsList,
  clearSelectedCollections,
  fetchCollections,
} from '../../actions/Collections';
import FilesGrid from '../../components/Files/Grid';
import FilesList from '../../components/Files/List';
import FilesMasonry from '../../components/Files/Masonry';
import FilesTile from '../../components/Files/Tile';
import FilesSelectAll from '../../components/Files/SelectAll';
import Sort from '../../components/Filters/Sort';
import ViewMode from '../../components/Filters/ViewMode';
import Loading from '../../components/Loading';
import FilesMiniMasonry from '../../mini/components/Files/Masonry';

import { isMini } from '../../helpers/App';
import { getCurrentUser } from '../../helpers/Auth';
import { getFilesView } from '../../helpers/Files';
import { canDownloadPageItem } from '../../helpers/PageSections';
import { getUserSetting } from '../../helpers/Settings';
import { handleURLParams } from '../../helpers/URLParams';

const FileResultsSection = ({
  dispatch,
  files,
  fileRouteResolver,
  filesLoading,
  filesEnd,
  filesParams,
  search,
  collection,
  filesTotal,
  handleMoveSelectedItems,
  onLoadMore,
  onRefresh,
  displayOnly,
  hideSaveSort,
  allowCheck,
  renderDisplayAction,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [filesExpanded, setFilesExpanded] = useState(true);

  const savedFilesView = (displayOnly && new URLSearchParams(location.search).get('view')) || getFilesView();
  const filesView = displayOnly && savedFilesView === 'list' ? 'grid' : savedFilesView;
  const displayFiles = fileRouteResolver
    ? files.map(file => ({ ...file, to: fileRouteResolver(file) }))
    : files;
  const selectableFiles = displayOnly ? displayFiles.filter(canDownloadPageItem) : displayFiles;
  const canCheckFile = displayOnly ? file => allowCheck && canDownloadPageItem(file) : allowCheck;

  const getLoadedFilesLabel = () => {
    if (filesTotal === 0) {
      return null;
    }

    return intl.formatMessage({ defaultMessage: '({files} of {filesTotal})' }, { files: files.length, filesTotal });
  };

  const handleSwitchFilesExpanded = (e) => {
    e.preventDefault();
    setFilesExpanded(!filesExpanded);
  };

  const {
    orderby,
    order,
  } = filesParams;

  const setOrder = (newOrderby, newOrder) => {
    if (onRefresh) {
      dispatch(setFilesParams({ orderby: newOrderby, order: newOrder }));
      onRefresh();
      return;
    }
    dispatch(clearFiles());
    dispatch(setFilesParams({
      orderby: newOrderby,
      order: newOrder,
      page: 0,
    }));
    dispatch(fetchFiles());
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(fetchCollections());
  };

  const setFilesView = (view) => {
    if (displayOnly) {
      handleURLParams({ view }, history, location.state);
      return;
    }
    const profile = getCurrentUser();
    dispatch(clearFilesList());
    dispatch(setFilesLoading(true));
    dispatch(setUserSetting({ files_view: view }));
    if (profile.uuid) {
      dispatch(updateUserSettings(profile.uuid, { files_view: view })).then(() => {
        if (onRefresh) {
          onRefresh();
          return;
        }
        dispatch(setFilesParams({ page: 0 }));
        dispatch(fetchFiles());
      });
    } else {
      if (onRefresh) {
        onRefresh();
        return;
      }
      dispatch(setFilesParams({ page: 0 }));
      dispatch(fetchFiles());
    }
  };

  return (
    <>
      <div className="collection-tools">
        <div className="list-switch">
          <a
            href="#files-switch"
            onClick={handleSwitchFilesExpanded}
          >
            {intl.formatMessage({ defaultMessage: 'Files {label}' }, { label: getLoadedFilesLabel() })}{' '}
            {filesExpanded ? (
              <i className="far fa-chevron-down" />
            ) : (
              <i className="far fa-chevron-right" />
            )}
          </a>
        </div>
        <div className="collection-tools-actions">
          <div className="collection-tools-actions-buttons">
            <Sort
              orderby={orderby}
              order={order}
              search={search}
              setOrder={setOrder}
              hideSave={hideSaveSort}
              collection={collection}
              size="sm"
            />
            <ViewMode
              view={filesView}
              setView={setFilesView}
              size="sm"
              {...(displayOnly ? { allowedViews: ['grid', 'framed', 'masonry', 'tile'] } : {})}
            />
          </div>
          {filesExpanded && (
            <FilesSelectAll
              files={selectableFiles}
              {...(displayOnly ? { share: null } : {})}
            />
          )}
        </div>
      </div>
      {filesExpanded && (
        <>
          {(files.length > 0 || filesLoading) ? (
            <div className="files-container">
              {isMini() ? (
                getUserSetting('files_mini_view') === 'masonry' && (
                  <FilesMiniMasonry
                    files={files}
                  />
                )
              ) : (
                <>
                  {['grid', 'framed'].includes(filesView) && (
                    <FilesGrid
                      files={displayFiles}
                      handleMoveSelectedItems={handleMoveSelectedItems}
                      displayOnly={displayOnly}
                      allowCheck={canCheckFile}
                      renderDisplayAction={renderDisplayAction}
                      selectedView={filesView}
                    />
                  )}
                  {filesView === 'list' && (
                    <FilesList
                      files={displayFiles}
                      handleMoveSelectedItems={handleMoveSelectedItems}
                      displayOnly={displayOnly}
                      allowCheck={canCheckFile}
                      renderDisplayAction={renderDisplayAction}
                    />
                  )}
                  {filesView === 'masonry' && (
                    <FilesMasonry
                      files={displayFiles}
                      handleMoveSelectedItems={handleMoveSelectedItems}
                      displayOnly={displayOnly}
                      allowCheck={canCheckFile}
                      renderDisplayAction={renderDisplayAction}
                    />
                  )}
                  {filesView === 'tile' && (
                    <FilesTile
                      files={displayFiles}
                      handleMoveSelectedItems={handleMoveSelectedItems}
                      displayOnly={displayOnly}
                      allowCheck={canCheckFile}
                      renderDisplayAction={renderDisplayAction}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            filesEnd && (
              <div>
                {intl.formatMessage({ defaultMessage: 'No results' })}
              </div>
            )
          )}
          {!filesLoading && (
            <Waypoint
              bottomOffset="-400px"
              onEnter={() => {
                if (!filesEnd) {
                  if (onLoadMore) {
                    onLoadMore();
                  } else {
                    dispatch(fetchFiles());
                  }
                }
              }}
            />
          )}
          {filesLoading && (
            <Loading />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  filesLoading: state.Files.loading,
  filesEnd: state.Files.end,
  selectedFiles: state.Files.selectedFiles,
  filesParams: state.Files.params,
  search: state.Search.search,
  collection: state.Collections.collection,
});

FileResultsSection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  filesTotal: PropTypes.number.isRequired,
  fileRouteResolver: PropTypes.func,
  filesLoading: PropTypes.bool.isRequired,
  filesEnd: PropTypes.bool.isRequired,
  filesParams: PropTypes.object.isRequired,
  search: PropTypes.string,
  collection: PropTypes.object,
  handleMoveSelectedItems: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func,
  onRefresh: PropTypes.func,
  displayOnly: PropTypes.bool,
  hideSaveSort: PropTypes.bool,
  allowCheck: PropTypes.bool,
  renderDisplayAction: PropTypes.func,
};

FileResultsSection.defaultProps = {
  onRefresh: null,
  displayOnly: false,
  hideSaveSort: false,
  allowCheck: true,
  renderDisplayAction: null,
  fileRouteResolver: null,
  collection: null,
  search: null,
};

export default withRouter(connect(mapStateToProps)(FileResultsSection));
